import { useCallback, useState } from 'react'
import { useCurrentOrg } from '@/app/entities/org/model'
import { payWithCloudPayments, useCloudPaymentsWidget } from '@/app/entities/cloudPayments'
import { closeInsufficientBalanceModal } from '@/app/entities/insufficientBalance/model'
import { openPaymentResultModal, type PaymentResultReopenSource } from '@/app/entities/paymentResult'
import { subscriptionPaymentDataApiV1CloudpaymentsSubscribeSubscriptionIdGet } from '@/common/api/client/sdk.gen'
import { markSubscriptionPaymentStarted } from '@/common/lib/yandexMetrica/paymentGoals'

type SubscribePurchaseMeta = {
    purchaseLabel: string
    tokens?: number
}

type UseSubscribePaymentOptions = {
    onSuccess?: () => void
    reopenSource?: PaymentResultReopenSource
}

export const useSubscribePayment = ({ onSuccess, reopenSource }: UseSubscribePaymentOptions = {}) => {
    const { data: currentOrg } = useCurrentOrg()
    const [isPaying, setIsPaying] = useState(false)
    const [payingSubscriptionId, setPayingSubscriptionId] = useState<string | null>(null)
    const [paymentError, setPaymentError] = useState<string | null>(null)

    const { isWidgetReady, widgetError, getWidgetInstance } = useCloudPaymentsWidget({
        onClose: () => {
            setIsPaying(false)
            setPayingSubscriptionId(null)
        },
    })

    const handleSubscribe = useCallback(
        async (subscriptionId: string, purchaseMeta?: SubscribePurchaseMeta) => {
            if (isPaying) return
            if (!currentOrg?.id) {
                setPaymentError('Не удалось определить организацию.')
                return
            }
            if (!isWidgetReady) {
                setPaymentError('Платежный виджет еще не загружен.')
                return
            }

            const widget = getWidgetInstance()
            if (!widget) {
                setPaymentError('Платежный виджет не инициализировался.')
                return
            }

            setPaymentError(null)
            setIsPaying(true)
            setPayingSubscriptionId(subscriptionId)

            try {
                const response = await subscriptionPaymentDataApiV1CloudpaymentsSubscribeSubscriptionIdGet({
                    path: { subscription_id: subscriptionId },
                    query: { organization_id: currentOrg.id },
                })

                const paymentData = response.data
                if (!paymentData) {
                    setIsPaying(false)
                    setPayingSubscriptionId(null)
                    setPaymentError('Не удалось получить данные для оплаты.')
                    return
                }

                const purchaseLabel = purchaseMeta?.purchaseLabel || paymentData.description
                const amount = paymentData.amount
                const tokens = purchaseMeta?.tokens

                markSubscriptionPaymentStarted()

                payWithCloudPayments({
                    widget,
                    options: {
                        publicId: paymentData.public_id,
                        description: paymentData.description,
                        amount: paymentData.amount,
                        currency: paymentData.currency,
                        invoiceId: paymentData.invoice_id,
                        accountId: paymentData.account_id,
                        tokenize: true,
                        ...(paymentData.user_email ? { email: paymentData.user_email } : {}),
                        data: paymentData.data,
                    },
                    onSuccess: () => {
                        setIsPaying(false)
                        setPayingSubscriptionId(null)
                        if (reopenSource === 'insufficient-balance') {
                            closeInsufficientBalanceModal()
                        }
                        openPaymentResultModal({
                            status: 'success',
                            purchaseType: 'subscription',
                            purchaseLabel,
                            amount,
                            tokens,
                            reopenSource,
                            onSettledSuccess: onSuccess,
                        })
                    },
                    onFail: result => {
                        setIsPaying(false)
                        setPayingSubscriptionId(null)
                        if (reopenSource === 'insufficient-balance') {
                            closeInsufficientBalanceModal()
                        }
                        openPaymentResultModal({
                            status: 'fail',
                            purchaseType: 'subscription',
                            purchaseLabel,
                            amount,
                            tokens,
                            failReason: result.reason,
                            reopenSource,
                        })
                    },
                })
            } catch {
                setIsPaying(false)
                setPayingSubscriptionId(null)
                setPaymentError('Не удалось получить данные для оплаты.')
            }
        },
        [currentOrg?.id, getWidgetInstance, isPaying, isWidgetReady, onSuccess, reopenSource]
    )

    return {
        handleSubscribe,
        isPaying,
        payingSubscriptionId,
        isWidgetReady,
        paymentError: paymentError ?? widgetError,
    }
}
